import React, { useState, useEffect } from 'react';
import { DollarSign, TrendingUp, History, Calculator, Download, RefreshCw } from 'lucide-react';
import PricingTiersCard from './PricingTiersCard';
import PricingAdjustmentsCard from './PricingAdjustmentsCard';
import PriceIncreaseModal from './PriceIncreaseModal';
import PricingHistoryView from './PricingHistoryView';
import PricingCalculator from './PricingCalculator';
import LoadingSpinner from '../LoadingSpinner';
import ErrorMessage from '../ErrorMessage';
import { pricingService } from '../../services/pricingService';
import './PricingDashboard.css';

const PricingDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('current');
  const [pricingTiers, setPricingTiers] = useState([]);
  const [pricingAdjustments, setPricingAdjustments] = useState([]);
  const [showIncreaseModal, setShowIncreaseModal] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    loadPricingData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const loadPricingData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [tiers, adjustments] = await Promise.all([
        pricingService.getPricingTiers(),
        pricingService.getPricingAdjustments()
      ]);

      setPricingTiers(tiers);
      setPricingAdjustments(adjustments);

      // Find most recent update
      const allDates = [...tiers, ...adjustments]
        .map(item => new Date(item.updated_at))
        .filter(d => !isNaN(d));
      if (allDates.length > 0) {
        setLastUpdated(new Date(Math.max(...allDates)));
      }
    } catch (err) {
      console.error('Error loading pricing data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyIncrease = () => {
    setShowIncreaseModal(true);
  };

  const handleIncreaseApplied = () => {
    setShowIncreaseModal(false);
    setRefreshKey(prev => prev + 1);
  };

  const handleExportHistory = async () => {
    try {
      const csv = await pricingService.exportPricingHistory();
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pricing-history-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting history:', err);
      setError('Failed to export pricing history');
    }
  };

  const handleUpdateTier = async (tierId, updates, reason) => {
    try {
      await pricingService.updatePricingTier(tierId, updates, reason);
      setRefreshKey(prev => prev + 1);
    } catch (err) {
      console.error('Error updating tier:', err);
      setError(err.message);
    }
  };

  const handleUpdateAdjustment = async (adjustmentId, updates, reason) => {
    try {
      await pricingService.updatePricingAdjustment(adjustmentId, updates, reason);
      setRefreshKey(prev => prev + 1);
    } catch (err) {
      console.error('Error updating adjustment:', err);
      setError(err.message);
    }
  };

  const formatLastUpdated = () => {
    if (!lastUpdated) return null;
    return lastUpdated.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="pricing-dashboard">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="pricing-dashboard">
        <ErrorMessage message={error} onRetry={loadPricingData} />
      </div>
    );
  }

  return (
    <div className="pricing-dashboard">
      <div className="pricing-header">
        <div className="pricing-header-left">
          <DollarSign size={28} className="pricing-header-icon" />
          <div className="pricing-header-text">
            <h1>Pricing Configuration</h1>
            {lastUpdated && (
              <span className="last-updated">Last updated: {formatLastUpdated()}</span>
            )}
          </div>
        </div>
        <div className="pricing-header-actions">
          <button
            className="pricing-btn secondary"
            onClick={() => setRefreshKey(prev => prev + 1)}
            title="Refresh data"
          >
            <RefreshCw size={18} />
          </button>
          <button
            className="pricing-btn secondary"
            onClick={handleExportHistory}
            title="Export pricing history"
          >
            <Download size={18} />
            <span>Export</span>
          </button>
          <button
            className="pricing-btn primary"
            onClick={handleApplyIncrease}
          >
            <TrendingUp size={18} />
            <span>Apply % Increase</span>
          </button>
        </div>
      </div>

      <div className="pricing-tabs">
        <button
          className={`pricing-tab ${activeTab === 'current' ? 'active' : ''}`}
          onClick={() => setActiveTab('current')}
        >
          <DollarSign size={16} />
          Current Pricing
        </button>
        <button
          className={`pricing-tab ${activeTab === 'calculator' ? 'active' : ''}`}
          onClick={() => setActiveTab('calculator')}
        >
          <Calculator size={16} />
          Calculator
        </button>
        <button
          className={`pricing-tab ${activeTab === 'history' ? 'active' : ''}`}
          onClick={() => setActiveTab('history')}
        >
          <History size={16} />
          History
        </button>
      </div>

      <div className="pricing-content">
        {activeTab === 'current' && (
          <div className="pricing-current-view">
            <div className="pricing-section">
              <div className="pricing-section-header">
                <h2>Acreage-Based Pricing</h2>
                <span className="pricing-section-subtitle">Base cost calculated from project size</span>
              </div>
              <PricingTiersCard
                tiers={pricingTiers}
                onUpdate={handleUpdateTier}
              />
            </div>

            <div className="pricing-section">
              <div className="pricing-section-header">
                <h2>Add-ons & Adjustments</h2>
                <span className="pricing-section-subtitle">Fixed prices and percentage modifiers</span>
              </div>
              <PricingAdjustmentsCard
                adjustments={pricingAdjustments}
                onUpdate={handleUpdateAdjustment}
              />
            </div>
          </div>
        )}

        {activeTab === 'calculator' && (
          <PricingCalculator
            tiers={pricingTiers}
            adjustments={pricingAdjustments}
          />
        )}

        {activeTab === 'history' && (
          <PricingHistoryView />
        )}
      </div>

      {showIncreaseModal && (
        <PriceIncreaseModal
          onClose={() => setShowIncreaseModal(false)}
          onApply={handleIncreaseApplied}
        />
      )}
    </div>
  );
};

export default PricingDashboard;